"use client";

import { API_MODE } from "@/lib/api/helpers";
import { useScanProfiles } from "@/lib/hooks/use-scans";

/**
 * 2.854: does this DEPLOYMENT deliver AI-assisted testing? (The accepted
 * design, guide/proposals/ai-settings-visibility.md on ui-53-ai-nav-design,
 * a7d83052.)
 *
 * ONE signal: the served scan-profile menu (`GET /v1/scan-profiles`, the same
 * read and the same cache entry the scan-profile picker uses) LISTS the AI
 * Penetration Test profile. The gateway lists it exactly when the deployment's
 * roster delivers CAP-011 (`scanProfileMenu` → `IsAvailable`; the opt-in keeps
 * that path, cap011-strix-optin.md §2). The console holds no roster of its own
 * (invariant 14): it reads the platform's answer and nothing else.
 *
 * KEYED ON "LISTED AT ALL", NEVER ON AN OFFERED OR WITHHELD FIELD. S-416 will
 * withhold the profile per organisation (an org whose project switch is off or
 * whose limit is zero). The design's shape (a) keeps the profile LISTED for a
 * deployment that delivers it, with `offered: false` and a `withheld_reason`.
 * Keying the settings on "offered" would hide the very switch and limit that
 * end the withholding, so a listed-but-withheld entry still reads `available`
 * here. Only the scan-profile picker follows "offered".
 *
 * FAIL-OPEN, as 2.795's permission gate is: loading, or a read that FAILED for
 * any reason (a refusal, a 503, a dropped connection), is `unknown`, and
 * `unknown` shows the settings. Hiding a control the person may need on a read
 * that said nothing is the worse error; the settings' own routes still refuse
 * what the platform refuses.
 */
export type AiTestingAvailability = "available" | "unavailable" | "unknown";

/**
 * The profile's id as the gateway publishes it (`scanprofile.AIPenetrationTest`,
 * control/internal/scanprofile/scanprofile.go). The mock menu is fixture data
 * with ids of its own (lib/mocks/db.ts), so mock mode keys on the mock's id:
 * there, the mock menu decides, exactly as the served menu does over HTTP.
 */
export const AI_PENETRATION_TEST_PROFILE_ID = API_MODE === "http" ? "ai-penetration-test" : "sp-ai";

/**
 * `read: false` skips the menu read entirely and answers "unknown" (shown):
 * for a caller who cannot read the menu at all, such as a platform admin
 * (scanprofileapi.ListRequirement lets only the caller's own organisation read
 * it), so no refused request is sent that nothing would read.
 */
export function useAiTestingAvailability(options: { read?: boolean } = {}): AiTestingAvailability {
  const { data, isError } = useScanProfiles({ enabled: options.read ?? true });
  if (isError || !data) return "unknown";
  return data.some((p) => p.id === AI_PENETRATION_TEST_PROFILE_ID) ? "available" : "unavailable";
}

/** Whether the AI testing settings are shown: everything but a definite "not listed". */
export function useAiTestingShown(options: { read?: boolean } = {}): boolean {
  return useAiTestingAvailability(options) !== "unavailable";
}
